import { useState } from 'react';
import { TransactionItem, TransactionItemData } from './transaction-item';
import { TransactionDetails } from './transaction-details';
import { ChevronDown } from 'lucide-react';
import { cn } from "@/lib/utils";

interface TransactionsListProps {
  transactions: TransactionItemData[];
  currency: string;
  onTransactionClick?: (transaction: TransactionItemData) => void;
  emptyMessage?: string;
  selectedTransactionId?: string;
  expandable?: boolean;
}

export function TransactionsList({
  transactions,
  currency,
  onTransactionClick,
  emptyMessage = 'No transactions found',
  selectedTransactionId,
  expandable = true,
}: TransactionsListProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  if (!transactions || transactions.length === 0) {
    return <div className="text-muted-foreground py-8 text-center text-sm">{emptyMessage}</div>;
  }

  const handleTransactionClick = (transaction: TransactionItemData) => {
    if (expandable) {
      setExpandedId(expandedId === transaction.id ? null : transaction.id);
    }
    if (onTransactionClick) {
      onTransactionClick(transaction);
    }
  };

  return (
    <div className="space-y-2">
      {transactions.map((transaction) => {
        const isExpanded = expandedId === transaction.id;

        return (
          <div key={transaction.id} className="space-y-2">
            <div className="relative">
              <TransactionItem
                transaction={transaction}
                currency={currency}
                onClick={expandable ? handleTransactionClick : onTransactionClick}
                isSelected={selectedTransactionId === transaction.id || isExpanded}
              />

              {/* Expand indicator */}
              {expandable && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleTransactionClick(transaction);
                  }}
                  className="hover:bg-muted absolute right-3 top-3 rounded-md p-1 transition-colors"
                  aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
                >
                  <ChevronDown
                    className={cn(
                      'text-muted-foreground h-4 w-4 transition-transform',
                      isExpanded && 'rotate-180',
                    )}
                  />
                </button>
              )}
            </div>

            {/* Expanded details */}
            {isExpanded && expandable && (
              <div className="ml-11 animate-in slide-in-from-top-2">
                <TransactionDetails transaction={transaction} currency={currency} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
